import React, { useRef } from "react";
import {
  Circleiconstyle,
  Tourtext,
  ViewNext,
  GuidelineName,
  GuidelineWrapper,
} from "../../atoms/ConnectStories";
import { AddCloseIcon } from "../MediaPopup/MediaStyled";
import { isMobile } from "../../../utilities/deviceUtilities";

// Image assets
import CIRCLEICON from "../../../images/circle_icon.svg";
import RIGHTARROW from "../../../images/connect_arrow_right.svg";
import CLOSEICON from "../../../images/close-Icon-black.svg";

import { useTranslation } from "react-i18next";
import Overlay from "react-bootstrap/Overlay";
import Tooltip from "react-bootstrap/Tooltip";

/**
 * GuidelineIcon Component
 * -----------------------
 * - Displays a circular icon with blinking effect until opened.
 * - Shows a tooltip overlay with guideline content when active.
 * - Provides "Close" and "View Next" actions to control guideline flow.
 *
 * Props:
 * @param {Object} guideline - Guideline object containing { name, isActive, isBlinking }.
 * @param {Function} onViewNext - Handler for moving to the next guideline.
 * @param {Function} onCloseGuideline - Handler for closing the current guideline.
 * @param {boolean} isLastItem - Flag indicating if this is the last guideline.
 */
export default function GuidelineIcon({
  guideline,
  onViewNext,
  onCloseGuideline,
  isLastItem,
}) {
  const { name, isActive, isBlinking, placement } = guideline;
  const { t } = useTranslation();
  const width = isMobile(768) ? "325px": "465px" ;

  // Ref for positioning the overlay relative to the circle icon
  const iconRef = useRef(null);

  const onViewNextClick = () => {
    if (iconRef.current) {
      iconRef.current.scrollIntoView({
        behavior: "smooth", // smooth scrolling
        block: "start", // align to top of viewport
      });
    }
    onViewNext();
  };

  return (
    <>
      {/* Circle icon with blinking effect */}
      <Circleiconstyle
        ref={iconRef}
        src={CIRCLEICON}
        alt="guideline icon"
        isBlinking={isBlinking}
      />

      {/* Tooltip overlay shown when guideline is active */}
      <Overlay target={iconRef.current} show={isActive} placement={placement}>
        {(props) => (
          <Tooltip
            className="guideline-tooltip"
            id={`tour-tooltip-${name}`}
            {...props}
            style={{
              ...props.style,
              // Set Bootstrap tooltip max width via CSS variable
              "--bs-tooltip-max-width": width,
              opacity: 1,
            }}
          >
            <Tourtext>
              {/* Close button */}
              <AddCloseIcon onClick={onCloseGuideline}>
                <img src={CLOSEICON} alt="close" />
              </AddCloseIcon>

              {/* Guideline content */}
              <GuidelineWrapper>
                <GuidelineName>{t(name)}</GuidelineName>

                {/* Show "View Next" only if not the last guideline */}
                {!isLastItem && (
                  <ViewNext onClick={onViewNextClick}>
                    {t("ViewNext")} <img src={RIGHTARROW} alt="next" />
                  </ViewNext>
                )}
              </GuidelineWrapper>
            </Tourtext>
          </Tooltip>
        )}
      </Overlay>
    </>
  );
}
